import {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  ReactNode,
} from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "react-query";
import modificationService from "../../../services/modification.service";
import { ModificatorType } from "../Types/ModificatorType";
import { OnUpdateParams } from "../Types/OnUpdateParams";

interface ModificationsContextType {
  options: any[];
  featureGroup: ModificatorType[];
  certification: any;
  modifications: any[];
  isFetching: boolean;
  addGroup: (data: ModificatorType) => void;
  removeCategory: (categoryId: number) => void;
  updateItems: (params: OnUpdateParams) => void;
  updateNotes: (groupType: number, value: string) => void;
  refetch: () => void
}

const ModificationsContext = createContext<ModificationsContextType | null>(
  null
);

export const useModifications = () => {
  const ctx = useContext(ModificationsContext);
  if (!ctx)
    throw new Error("useModifications debe usarse dentro del proveedor");
  return ctx;
};

export const ModificationsProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [options, setOptions] = useState([]);
  const [featureGroup, setFeatureGroup] = useState<ModificatorType[]>([]);

  const { id } = useParams();

  const { data, isFetching, refetch } = useQuery(
    ["certifications", id],
    () => modificationService.getModification(id),
    { refetchOnWindowFocus: false, enabled: Number(id) > 0 }
  );

  const { modifications, certification } = useMemo(() => {
    return {
      modifications: data?.modifications ?? [],
      certification: data?.certification ?? null,
    };
  }, [data]);

  const fetchTypes = async () => {
    const types = await modificationService.getTypes();
    const selectedTypeIds = featureGroup.map((f) => f.type);
    const options = types.map((type) => ({
      label: type.name,
      value: type.id,
      disabled: selectedTypeIds.includes(type.id),
    }));
    setOptions(options);
  };

  useEffect(() => {
    if (modifications?.length > 0) {
      const groups: ModificatorType[] = modifications.map((mod: any) => ({
        name: "",
        chassis_status: mod.chassis_status,
        observation: mod.observation,
        type: mod.type,
        data: mod.features.map((feature) => ({
          id: feature.id,
          value: feature.pivot.value,
        })),
      }));

      groups.forEach((element) => addGroup(element));
    }
  }, [modifications]);

  useEffect(() => {
    fetchTypes();
  }, [featureGroup]);

  useEffect(() => {
    fetchTypes();
  }, [featureGroup]);
  const addGroup = (data: ModificatorType) => {
    setFeatureGroup((prevGroups) => {
      const isExist = prevGroups.find((group) => group.type === data.type);
      if (isExist || typeof data.type === "undefined") return prevGroups;

      const updated = [
        ...prevGroups,
        {
          ...data,
          data: data.data?.length > 0 ? data.data : [],
        },
      ];

      return updated;
    });
  };

  const removeCategory = (categoryId: number) => {
    setFeatureGroup((prev) =>
      prev.filter((feature) => feature.type !== categoryId)
    );
  };

  const updateItems = (params: OnUpdateParams) => {
    const { groupType, featureId, value } = params;

    setFeatureGroup((prevGroups) =>
      prevGroups.map((group) => {
        if (group.type !== groupType) return group;

        const existingItemIndex = group.data.findIndex(
          (item) => item.id === featureId
        );

        let newData;
        if (existingItemIndex !== -1) {
          newData = [...group.data];
          newData[existingItemIndex] = {
            ...newData[existingItemIndex],
            value,
          };
        } else {
          newData = [...group.data, { id: featureId, value }];
        }

        return {
          ...group,
          data: newData,
        };
      })
    );
  };

  const updateNotes = (groupType: number, value: string) => {
    setFeatureGroup((prevGroups) =>
      prevGroups.map((group) =>
        group.type === groupType ? { ...group, observation: value } : group
      )
    );
  };

  return (
    <ModificationsContext.Provider
      value={{
        options,
        featureGroup,
        certification,
        modifications,
        isFetching,
        addGroup,
        removeCategory,
        updateItems,
        updateNotes,
        refetch,
      }}
    >
      {children}
    </ModificationsContext.Provider>
  );
};
